import './App.css'
import { lazy, Suspense } from 'react';
import Toaster from '@/components/OnDemandToaster';
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
const VisualEditAgent = lazy(() => import('@/lib/VisualEditAgent'));
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import InitialLanguageRedirect from '@/components/InitialLanguageRedirect';
import { effectiveEntryPath, requestedEntryPath, initialLanguageTarget, getAutoLanguageTarget } from '@/components/entryLanguage';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import RootErrorBoundary from '@/components/RootErrorBoundary';
import PublicThemeFrame from '@/components/redesign/PublicThemeFrame';
import PrivateRouteMetadata from '@/components/seo/PrivateRouteMetadata';
import { OrganizationSchema } from '@/components/JsonLd';
// ── All explicitly-routed pages are lazy-loaded (own chunks) to keep the
// initial bundle small. The Suspense wrapper around <Routes> shows PageLoader
// during the brief chunk fetch on navigation. ──
const ElaftaleRedirect = lazy(() => import('@/pages/ElaftaleRedirect'));
const SupportFaqRedirect = lazy(() => import('@/pages/SupportFaqRedirect'));
const PrissammenligningRedirect = lazy(() => import('@/pages/PrissammenligningRedirect'));
const ShortUrlRedirectPage = lazy(() => import('@/pages/ShortUrlRedirect'));
const CheckDinElregning = lazy(() => import('@/pages/Check-din-elregning'));
const SammenlignHub = lazy(() => import('@/pages/SammenlignHub'));
const SammenlignPage = lazy(() => import('@/pages/SammenlignPage'));
const Poll = lazy(() => import('@/pages/Poll'));
const PollDetails = lazy(() => import('@/pages/PollDetails'));
const Opladerbetaling = lazy(() => import('@/pages/Opladerbetaling'));
const OpladerbetalingBekraeftelse = lazy(() => import('@/pages/OpladerbetalingBekraeftelse'));
const Henvisning = lazy(() => import('@/pages/Henvisning'));
const PrisgarantiApp = lazy(() => import('@/pages/PrisgarantiApp'));
const ElaftaleTransparent = lazy(() => import('@/pages/Elaftale'));
const EnkelKvartal = lazy(() => import('@/pages/Enkel-kvartal'));
const EnkelStrom = lazy(() => import('@/pages/Enkel-strom'));
const EnkelNatur = lazy(() => import('@/pages/Enkel-natur'));
const EnkelStromForbrug = lazy(() => import('@/pages/Enkel-strom-forbrug'));
const EnkelNaturForbrug = lazy(() => import('@/pages/Enkel-natur-forbrug'));
const Velkommen = lazy(() => import('@/pages/Velkommen'));
const PowerfuelVelkommen = lazy(() => import('@/pages/PowerfuelVelkommen'));
const PowerfuelVelkommenEmailPreview = lazy(() => import('@/pages/PowerfuelVelkommenEmailPreview'));
const MarketingForside = lazy(() => import('@/pages/Marketing-forside'));
const BlogArtikelHenvisning = lazy(() => import('@/pages/BlogArtikelHenvisning'));
const EltjekEnkelKvartal = lazy(() => import('@/pages/EltjekEnkelKvartal'));
const EltjekEnkelStrom = lazy(() => import('@/pages/EltjekEnkelStrom'));
const EltjekEnkelNatur = lazy(() => import('@/pages/EltjekEnkelNatur'));
const EVForside = lazy(() => import('@/pages/EVForside'));
const NyCheckout = lazy(() => import('@/pages/NyCheckout'));
const DetailedCheckoutRedirect = lazy(() => import('@/pages/DetailedCheckoutRedirect'));
const ServiceSmartLaderabat = lazy(() => import('@/pages/ServiceSmartLaderabat'));
const ArkivForside = lazy(() => import('@/pages/ArkivForside'));
const ArkivElaftale = lazy(() => import('@/pages/ArkivElaftale'));
const ArkivSmartCharge = lazy(() => import('@/pages/ArkivSmartCharge'));
const ArkivLaderabat = lazy(() => import('@/pages/ArkivLaderabat'));
const ArkivServiceAftale = lazy(() => import('@/pages/ArkivServiceAftale'));
const BlogArtikelElbilejersGuldaeg = lazy(() => import('@/pages/BlogArtikelElbilejersGuldaeg'));
const HenvisningApp = lazy(() => import('@/pages/HenvisningApp'));
const TestForside = lazy(() => import('@/pages/TestForside'));
const Test2Forside = lazy(() => import('@/pages/Test2Forside'));
const BlogArtikelProduktSurvey = lazy(() => import('@/pages/BlogArtikelProduktSurvey'));
const SmartChargePrisgaranti = lazy(() => import('@/pages/SmartChargePrisgaranti'));
const PortalPrisgaranti = lazy(() => import('@/pages/PortalPrisgaranti'));
const PortalSmartCharge = lazy(() => import('@/pages/PortalSmartCharge'));
const SmartChargeVilkaar = lazy(() => import('@/pages/SmartChargeVilkaar'));
const UnderstoettedeBilmaerker = lazy(() => import('@/pages/UnderstoettedeBilmaerker'));
const EnSupportedCarBrands = lazy(() => import('@/pages/en/EnSupportedCarBrands'));
// Start the English shell while the required public-settings check is running.
const englishEntryPath = effectiveEntryPath;
const englishEntryLayout = (englishEntryPath === '/en' || englishEntryPath.startsWith('/en/') || englishEntryPath === '/about-en') && englishEntryPath !== '/en/customer-portal' ? import('@/components/en/EnLayout') : null;
const englishEntryHome = englishEntryPath === '/en' ? import('@/pages/en/EnForside') : null;
const EnLayout = lazy(() => englishEntryLayout || import('@/components/en/EnLayout'));
const EnForside = lazy(() => englishEntryHome || import('@/pages/en/EnForside'));
const EnVelkommen = lazy(() => import('@/pages/en/EnVelkommen'));
const EnSolar = lazy(() => import('@/pages/en/EnSolar'));
const EnSupport = lazy(() => import('@/pages/en/EnSupport'));
const EnCustomerPortal = lazy(() => import('@/pages/en/EnCustomerPortal'));
const EnNyCheckout = lazy(() => import('@/pages/en/EnNyCheckout'));
const EnElaftale = lazy(() => import('@/pages/en/EnElaftale'));
const EnOpladere = lazy(() => import('@/pages/en/EnOpladere'));
const EnElprisSammenligning = lazy(() => import('@/pages/en/EnElprisSammenligning'));
const EnSmartChargePrisgaranti = lazy(() => import('@/pages/en/EnSmartChargePrisgaranti'));
const EnServiceSmartLaderabat = lazy(() => import('@/pages/en/EnServiceSmartLaderabat'));
const EnPrisTimeForTime = lazy(() => import('@/pages/en/EnPrisTimeForTime'));
const EnAppGuide = lazy(() => import('@/pages/en/EnAppGuide'));
const EnServiceAgreement = lazy(() => import('@/pages/en/EnServiceAgreement'));
const EnBlog = lazy(() => import('@/pages/en/EnBlog'));
const EnBlogArticle = lazy(() => import('@/pages/en/EnBlogArticle'));
const AboutEn = lazy(() => import('@/pages/About-en'));

// ── Internal/admin-only routes: lazy-loaded, kept out of the public bundle ──
const SEOGenerator = lazy(() => import('@/pages/SEOGenerator'));
const HenvisningAdmin = lazy(() => import('@/pages/HenvisningAdmin'));
const VelkommenEmailPreview = lazy(() => import('@/pages/VelkommenEmailPreview'));
const VelkommenProduktionEmailPreview = lazy(() => import('@/pages/VelkommenProduktionEmailPreview'));
const VelkomstAppEmailPreview = lazy(() => import('@/pages/VelkomstAppEmailPreview'));
const AppReminderEmailPreview = lazy(() => import('@/pages/AppReminderEmailPreview'));
const SurveyAdmin = lazy(() => import('@/pages/SurveyAdmin'));
const CsDashboard = lazy(() => import('@/pages/Cs-dashboard'));
const OpladereAdmin = lazy(() => import('@/pages/OpladereAdmin'));
const AiAssistantAdmin = lazy(() => import('@/pages/AiAssistantAdmin'));
const AppSurvey = lazy(() => import('@/pages/AppSurvey'));
const AppSurveyAdmin = lazy(() => import('@/pages/AppSurveyAdmin'));


const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

// Shown while an internal/admin route chunk downloads
const PageLoader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-[#F8F8F8]">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return <PageLoader />;
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Preserve auth checks, but do not first mount/download the wrong language.
  if (initialLanguageTarget && location.pathname === requestedEntryPath && getAutoLanguageTarget()) {
    return <InitialLanguageRedirect target={initialLanguageTarget} />;
  }

  // Render the main app
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={
        <LayoutWrapper currentPageName={mainPageKey}>
          <MainPage />
        </LayoutWrapper>
      } />
      {Object.entries(Pages).filter(([path]) => path !== 'About-en').map(([path, Page]) => (
        <Route
          key={path}
          path={`/${path}`}
          element={
            <LayoutWrapper currentPageName={path}>
              <Page />
            </LayoutWrapper>
          }
        />
      ))}
      <Route path="/check-din-elregning" element={
        <LayoutWrapper currentPageName="Check-din-elregning">
          <CheckDinElregning />
        </LayoutWrapper>
      } />
      <Route path="/sammenlign" element={
        <LayoutWrapper currentPageName="SammenlignHub">
          <SammenlignHub />
        </LayoutWrapper>
      } />
      <Route path="/SammenlignHub" element={
        <LayoutWrapper currentPageName="SammenlignHub">
          <SammenlignHub />
        </LayoutWrapper>
      } />
      <Route path="/sammenlign/:slug" element={
        <LayoutWrapper currentPageName="SammenlignPage">
          <SammenlignPage />
        </LayoutWrapper>
      } />
      <Route path="/SammenlignPage/:slug" element={
        <LayoutWrapper currentPageName="SammenlignPage">
          <SammenlignPage />
        </LayoutWrapper>
      } />
      <Route path="/SEOGenerator" element={
        <LayoutWrapper currentPageName="SEOGenerator">
          <SEOGenerator />
        </LayoutWrapper>
      } />
      <Route path="/poll" element={<Poll />} />
      <Route path="/poll-details" element={<PollDetails />} />
      <Route path="/opladerbetaling" element={<Opladerbetaling />} />
      <Route path="/opladerbetaling/bekraeftelse" element={
        <LayoutWrapper currentPageName="OpladerbetalingBekraeftelse">
          <OpladerbetalingBekraeftelse />
        </LayoutWrapper>
      } />
      <Route path="/henvisning" element={
        <LayoutWrapper currentPageName="Henvisning">
          <Henvisning />
        </LayoutWrapper>
      } />
      <Route path="/admin/henvisning" element={<HenvisningAdmin />} />
      <Route path="/prisgaranti-app" element={<PrisgarantiApp />} />
      <Route path="/elaftale" element={<LayoutWrapper currentPageName="Elaftale"><ElaftaleTransparent /></LayoutWrapper>} />
      <Route path="/enkel-kvartal" element={<LayoutWrapper currentPageName="Enkel-kvartal"><EnkelKvartal /></LayoutWrapper>} />
      <Route path="/enkel-strom" element={<LayoutWrapper currentPageName="Enkel-strom"><EnkelStrom /></LayoutWrapper>} />
      <Route path="/enkel-natur" element={<LayoutWrapper currentPageName="Enkel-natur"><EnkelNatur /></LayoutWrapper>} />
      <Route path="/enkel-strom-forbrug" element={<LayoutWrapper currentPageName="Enkel-strom-forbrug"><EnkelStromForbrug /></LayoutWrapper>} />
      <Route path="/enkel-natur-forbrug" element={<LayoutWrapper currentPageName="Enkel-natur-forbrug"><EnkelNaturForbrug /></LayoutWrapper>} />
      <Route path="/velkommen" element={<LayoutWrapper currentPageName="Velkommen"><Velkommen /></LayoutWrapper>} />
      <Route path="/powerfuel" element={<LayoutWrapper currentPageName="PowerfuelVelkommen"><PowerfuelVelkommen /></LayoutWrapper>} />
      <Route path="/admin/powerfuel-velkommen-email" element={<PowerfuelVelkommenEmailPreview />} />
      <Route path="/admin/velkommen-email" element={<VelkommenEmailPreview />} />
      <Route path="/admin/velkommen-produktion-email" element={<VelkommenProduktionEmailPreview />} />
      <Route path="/admin/velkomst-app-email" element={<VelkomstAppEmailPreview />} />
      <Route path="/admin/app-paamindelse-email" element={<AppReminderEmailPreview />} />
      <Route path="/marketing-forside" element={<LayoutWrapper currentPageName="Marketing-forside"><MarketingForside /></LayoutWrapper>} />
      <Route path="/blogartikelhenvisning" element={<LayoutWrapper currentPageName="BlogArtikelHenvisning"><BlogArtikelHenvisning /></LayoutWrapper>} />
      <Route path="/eltjek-enkel-kvartal" element={<LayoutWrapper currentPageName="EltjekEnkelKvartal"><EltjekEnkelKvartal /></LayoutWrapper>} />
      <Route path="/eltjek-enkel-strom" element={<LayoutWrapper currentPageName="EltjekEnkelStrom"><EltjekEnkelStrom /></LayoutWrapper>} />
      <Route path="/eltjek-enkel-natur" element={<LayoutWrapper currentPageName="EltjekEnkelNatur"><EltjekEnkelNatur /></LayoutWrapper>} />
      <Route path="/ev-forside" element={<LayoutWrapper currentPageName="EVForside"><EVForside /></LayoutWrapper>} />
      <Route path="/ny-checkout" element={<LayoutWrapper currentPageName="NyCheckout"><NyCheckout /></LayoutWrapper>} />
      <Route path="/detaljeret-checkout" element={<DetailedCheckoutRedirect />} />
      <Route path="/el-fordele" element={<LayoutWrapper currentPageName="ServiceSmartLaderabat"><ServiceSmartLaderabat /></LayoutWrapper>} />
      <Route path="/arkiv-forside" element={<LayoutWrapper currentPageName="ArkivForside"><ArkivForside /></LayoutWrapper>} />
      <Route path="/arkiv-elaftale" element={<LayoutWrapper currentPageName="ArkivElaftale"><ArkivElaftale /></LayoutWrapper>} />
      <Route path="/arkiv-smart-charge" element={<Navigate to="/portal/smart-charge" replace />} />
      <Route path="/arkiv-laderabat" element={<LayoutWrapper currentPageName="ArkivLaderabat"><ArkivLaderabat /></LayoutWrapper>} />
      <Route path="/arkiv-serviceaftale" element={<LayoutWrapper currentPageName="ArkivServiceAftale"><ArkivServiceAftale /></LayoutWrapper>} />
      <Route path="/elbilejeres-guldaeg" element={<LayoutWrapper currentPageName="BlogArtikelElbilejersGuldaeg"><BlogArtikelElbilejersGuldaeg /></LayoutWrapper>} />
      <Route path="/app-henvisning" element={<HenvisningApp />} />
      <Route path="/test-forside" element={<LayoutWrapper currentPageName="TestForside"><TestForside /></LayoutWrapper>} />
      <Route path="/test2-forside" element={<LayoutWrapper currentPageName="Test2Forside"><Test2Forside /></LayoutWrapper>} />
      <Route path="/opladere-visma" element={<Navigate to="/opladere" replace />} />
      {/* Easee Up is switched off (catalog status "hidden") – old links go to the charger overview */}
      <Route path="/easee-up" element={<Navigate to="/opladere" replace />} />
      <Route path="/understoettede-bilmaerker" element={<LayoutWrapper currentPageName="UnderstoettedeBilmaerker"><UnderstoettedeBilmaerker /></LayoutWrapper>} />
      <Route path="/admin/survey" element={<SurveyAdmin />} />
      <Route path="/blogartikel-produkt-survey" element={<LayoutWrapper currentPageName="BlogArtikelProduktSurvey"><BlogArtikelProduktSurvey /></LayoutWrapper>} />
      <Route path="/cs-dashboard" element={<CsDashboard />} />
      <Route path="/opladere/admin" element={<OpladereAdmin />} />
      <Route path="/admin/opladere" element={<OpladereAdmin />} />
      <Route path="/admin/aiassistant" element={<AiAssistantAdmin />} />
      <Route path="/app-survey" element={<AppSurvey />} />
      <Route path="/admin/app-survey" element={<AppSurveyAdmin />} />
      <Route path="/smart-charge-prisgaranti" element={<SmartChargePrisgaranti />} />
      <Route path="/portal-prisgaranti" element={<PortalPrisgaranti />} />
      <Route path="/portal/smart-charge" element={
        <LayoutWrapper currentPageName="PortalSmartCharge">
          <PortalSmartCharge />
        </LayoutWrapper>
      } />
      <Route path="/smart-charge-prisgaranti-vilkaar" element={<LayoutWrapper currentPageName="SmartChargeVilkaar"><SmartChargeVilkaar /></LayoutWrapper>} />
      <Route path="/el" element={<LayoutWrapper currentPageName="ElaftaleRedirect"><ElaftaleRedirect /></LayoutWrapper>} />
      <Route path="/new-faq" element={<LayoutWrapper currentPageName="SupportFaqRedirect"><SupportFaqRedirect /></LayoutWrapper>} />
      <Route path="/page" element={<LayoutWrapper currentPageName="PrissammenligningRedirect"><PrissammenligningRedirect /></LayoutWrapper>} />
      <Route path="/s" element={<ShortUrlRedirectPage />} />

      {/* English customer portal — standalone full-screen (outside EnLayout), like Danish /kundeportal */}
      <Route path="/en/customer-portal" element={<EnCustomerPortal />} />

      {/* ── English (/en) section — hard-coded English, own header/footer ── */}
      <Route element={<EnLayout />}>
        <Route path="/about-en" element={<AboutEn />} />
        <Route path="/en" element={<EnForside />} />
        <Route path="/en/welcome" element={<EnVelkommen />} />
        <Route path="/en/solar-agreement" element={<EnSolar />} />
        <Route path="/en/support" element={<EnSupport />} />
        <Route path="/en/ny-checkout" element={<EnNyCheckout />} />
        {/* electricity-deal is the MARKETING page (matches Danish /elaftale). Checkout is /en/ny-checkout. */}
        <Route path="/en/electricity-deal" element={<EnElaftale />} />
        {/* Full English versions of the secondary pages */}
        <Route path="/en/chargers" element={<EnOpladere />} />
        <Route path="/en/compare-electricity-companies" element={<EnElprisSammenligning />} />
        <Route path="/en/smart-charge-price-guarantee" element={<EnSmartChargePrisgaranti />} />
        <Route path="/en/electricity-benefits" element={<EnServiceSmartLaderabat />} />
        <Route path="/en/supported-car-brands" element={<EnSupportedCarBrands />} />
        <Route path="/en/electricity-price-hour-by-hour" element={<EnPrisTimeForTime />} />
        {/* English customer-portal subpages (app guide is customer-gated like the Danish /enkel-app) */}
        <Route path="/en/app-guide" element={<EnAppGuide />} />
        <Route path="/en/service-agreement" element={<EnServiceAgreement />} />
        {/* English blog */}
        <Route path="/en/blog" element={<EnBlog />} />
        <Route path="/en/blog/:slug" element={<EnBlogArticle />} />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <RootErrorBoundary>
      <Router>
        <AuthProvider>
          <QueryClientProvider client={queryClientInstance}>
            <NavigationTracker />
            <PublicThemeFrame><AuthenticatedApp /></PublicThemeFrame>
            <OrganizationSchema />
            <PrivateRouteMetadata />
            <Toaster />
            {window.self !== window.top && <Suspense fallback={null}><VisualEditAgent /></Suspense>}
          </QueryClientProvider>
        </AuthProvider>
      </Router>
    </RootErrorBoundary>
  )
}

export default App